import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { EmptyState, LoadingState, PageHeader, SectionHeading } from '../../components/ui/Primitives';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { timestampValue } from '../../utils/display';

export default function AdminDashboard() {
  const { user } = useAuth();
  const { users = [] } = useData() || {};
  const [leads, setLeads] = useState(null);
  const [proofs, setProofs] = useState(null);
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';
  useEffect(() => {
    if (!useFirebase) { setLeads([]); setProofs([]); return undefined; }
    const unsubLeads = onSnapshot(query(collection(db, 'leads'), orderBy('createdAt', 'desc')), snapshot => setLeads(snapshot.docs.map(item => ({ id: item.id, ...item.data() }))), () => setLeads([]));
    const unsubProofs = onSnapshot(query(collection(db, 'pops'), orderBy('createdAt', 'desc')), snapshot => setProofs(snapshot.docs.map(item => ({ id: item.id, ...item.data() }))), () => setProofs([]));
    return () => { unsubLeads(); unsubProofs(); };
  }, [useFirebase]);
  const needsAttention = useMemo(() => {
    const rows = [];
    if (leads) {
      const pending = leads.filter(lead => ['new', 'pending_approval', 'registered'].includes(lead.stage || 'new')).length;
      if (pending > 0) rows.push({ title: 'Pending applications', detail: `${pending} ${pending === 1 ? 'application' : 'applications'} awaiting review`, to: '/admin/applications', action: 'Review' });
    }
    if (proofs) {
      const pending = proofs.filter(proof => proof.status !== 'approved').length;
      if (pending > 0) rows.push({ title: 'Proof of payment', detail: `${pending} ${pending === 1 ? 'submission' : 'submissions'} awaiting review`, to: '/admin/finance?tab=proofs', action: 'Review' });
    }
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const timestampedStudents = users.filter(person => (person.roles || [person.role]).includes('student') && timestampValue(person.createdAt));
    if (timestampedStudents.length) {
      const recent = timestampedStudents.filter(person => timestampValue(person.createdAt) >= cutoff).length;
      if (recent > 0) rows.push({ title: 'Recent registrations', detail: `${recent} new ${recent === 1 ? 'registration' : 'registrations'} in the last 30 days`, to: '/admin/students', action: 'View' });
    }
    return rows;
  }, [leads, proofs, users]);
  const doors = [
    { title: 'Students', detail: 'Manage people and platform access', to: '/admin/students' },
    { title: 'Academics', detail: 'Faculties, courses and resources', to: '/admin/academics' },
    { title: 'Applications', detail: 'Review incoming applications', to: '/admin/applications' },
    { title: 'Finance', detail: 'Payments and financial administration', to: '/admin/finance' },
    { title: 'Website', detail: 'Manage public website content', to: '/admin/website' }
  ];

  return (
    <div>
      <PageHeader eyebrow="School of SHEM" title="Administration" description={`Welcome back, ${user?.firstName || 'Administrator'}.`} />
      <section className="workspace-section"><SectionHeading title="Needs attention" />{leads === null || proofs === null ? <LoadingState label="Checking administrative queues…" /> : needsAttention.length ? <div className="work-queue">{needsAttention.map(item => <div className="work-queue-row" key={item.title}><span><strong>{item.title}</strong><span>{item.detail}</span></span><Link className="ui-text-action" to={item.to}>{item.action} →</Link></div>)}</div> : <EmptyState title="Nothing needs immediate attention" description="New applications and payment reviews will appear here." />}</section>
      <section className="workspace-section"><SectionHeading title="Quick access" /><div className="quick-door-grid">{doors.map(door => <Link className="door-row" to={door.to} key={door.title}><span><strong>{door.title}</strong><span>{door.detail}</span></span><b aria-hidden="true">→</b></Link>)}</div></section>
    </div>
  );
}
